/**
 * Service Worker Registration and Push Notification utilities
 * For SyndicAppli PWA
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if service workers and push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Register Service Worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });
    
    console.log('Service Worker registered:', registration.scope);
    
    // Check for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New version available
          console.log('New version available');
        }
      });
    });
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Get VAPID public key from server
export const getVapidPublicKey = async (token) => {
  try {
    const response = await fetch(`${API_URL}/api/notifications/vapid-public-key`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.publicKey;
    }
  } catch (error) {
    console.error('Failed to get VAPID key:', error);
  }
  return null;
};

// Convert VAPID key to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Subscribe to push notifications
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return { success: false, reason: 'not_supported' };
  }

  try {
    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { success: false, reason: 'permission_denied' };
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey(token);
    if (!vapidPublicKey) {
      console.error('Failed to get VAPID public key');
      return { success: false, reason: 'vapid_error' };
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });

    // Send subscription to server
    const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription.toJSON())
    });

    if (response.ok) {
      console.log('Push subscription successful');
      return { success: true, subscription };
    } else {
      console.error('Failed to save subscription to server');
      return { success: false, reason: 'server_error' };
    }
  } catch (error) {
    console.error('Push subscription failed:', error);
    return { success: false, reason: error.message };
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();

      // Notify server
      await fetch(`${API_URL}/api/notifications/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Push unsubscription successful');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Push unsubscription failed:', error);
    return false;
  }
};

// Check if user is subscribed to push
export const isPushSubscribed = async () => {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

// Get notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'not_supported';
  return Notification.permission;
};

// Get notification preferences
export const getNotificationPreferences = async (token) => {
  try {
    const response = await fetch(`${API_URL}/api/notifications/preferences`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to get notification preferences:', error);
  }
  return null;
};

// Update notification preferences
export const updateNotificationPreferences = async (token, preferences) => {
  try {
    const response = await fetch(`${API_URL}/api/notifications/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(preferences)
    });
    
    return response.ok;
  } catch (error) {
    console.error('Failed to update notification preferences:', error);
    return false;
  }
};

// Send test notification
export const sendTestNotification = async (token) => {
  try {
    const response = await fetch(`${API_URL}/api/notifications/send/test`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error('Failed to send test notification:', error);
  }
  return null;
};
